/**
 * The product's own visit-status colours, for the two demo surfaces that draw a
 * plan visit as a chip: the patient calendar and the episode plan's track table.
 *
 * Pete, 2026-09-22: *"since color isn't used to communicate visit type anymore
 * we should use the completed/scheduled colors that match the rest of the
 * application"* — the status chip on a card under **Upcoming Visits** and the dot
 * on the **Visit timeline** both read `StatusBgColors` / `StatusBgColorsDark`
 * from `@caredna-marlin/utils/client`, through `~/utils/statuses`. My day
 * already draws a scheduled visit from the same pair; these two were the last
 * demo surfaces inventing their own greens and blues.
 *
 * A **reservation** has no product status to borrow, because the product has no
 * such thing. It takes the scheduled green thinned toward white — the same
 * relationship an overflow shift has to a shift on Who is working — so the fill
 * says *this is a visit that is not committed yet* without introducing a hue.
 */
import { StatusBgColors, StatusBgColorsDark } from "@caredna-marlin/utils/client";

/**
 * Delivered, and anything else already behind us.
 *
 * **Black text** (Pete, 2026-09-22). The product's dark blue is its *border and
 * icon* colour, not a text colour, and at 10px on a light-blue fill it read as a
 * link. The ordinary body colour is what a delivered visit's label wants.
 */
export const DONE = { bgcolor: StatusBgColors.completed, color: "#1d2328", border: `1px solid ${StatusBgColorsDark.completed}33` } as const;

/** Committed and still to come. */
export const SCHEDULED = { bgcolor: StatusBgColors.scheduled, color: StatusBgColorsDark.scheduled, border: `1px solid ${StatusBgColorsDark.scheduled}33` } as const;

/** Promised and not committed: the scheduled green at about 45% over white, dashed. */
export const RESERVED = { bgcolor: "#dcf8de", color: "#2f5c3a", border: "1px dashed #a9d9b0" } as const;
